import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { sendTutorShoutout } from '../../api/sparksApi';

const REASONS = ['Great focus', 'Brilliant effort', 'Kept going after a mistake', 'For good behaviour'];
const PANEL_W = 256;
const EDGE = 8;
// The result line floats over the session, so it clears itself.
const STATUS_MS = 5000;

/**
 * In-class Addy shout-out (§2.3): +100 Sparks, max 2 per child per day, reason required and
 * audited. The server checks the child joined a class today, so this cannot be sent outside class.
 * The panel and its result line are portalled to the page's top layer under the button, so the student's
 * mirrored screen in the tile can never cover or clip them. `onSent` runs after a shout-out lands (the
 * session pings the child's call so it shows straight away).
 */
export default function TutorShoutoutButton({ childId, childName, onSent }) {
  const buttonRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [custom, setCustom] = useState('');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const floating = open || !!status;

  useLayoutEffect(() => {
    if (!floating) return undefined;
    const place = () => {
      const r = buttonRef.current?.getBoundingClientRect();
      if (r) setAnchor({ top: r.bottom + 4, left: Math.max(EDGE, Math.min(r.left, window.innerWidth - PANEL_W - EDGE)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [floating]);

  useEffect(() => {
    if (!status) return undefined;
    const t = setTimeout(() => setStatus(null), STATUS_MS);
    return () => clearTimeout(t);
  }, [status]);

  if (!childId) return null;

  const send = async () => {
    const text = (custom.trim() || reason).slice(0, 200);
    if (text.length < 3) return;
    setBusy(true);
    setStatus(null);
    try {
      const r = await sendTutorShoutout(childId, text);
      if (r?.awarded?.length) {
        setStatus({ ok: true, text: `Sent! +${r.awarded[0].amount} ✨ for ${childName || 'the student'}` });
        onSent?.();
      } else setStatus({ ok: false, text: r?.message || 'Already at the daily limit for this student' });
      setOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setStatus({ ok: false, text: Array.isArray(msg) ? msg.join(', ') : msg || 'Could not send the shout-out' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tutor-shoutout" style={{ display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => { setOpen((o) => !o); setStatus(null); }}
        className="px-2 py-1 rounded-full text-xs font-semibold bg-[#FFD966] text-[#2F1E6B] hover:bg-[#ffe58a]"
        title="Send an Addy shout-out (+100 Sparks)"
        data-testid="tutor-shoutout-button"
      >
        ✨ Shout-out
      </button>
      {floating && anchor && createPortal(
        <div className="fixed z-[1000]" style={{ top: anchor.top, left: anchor.left, width: PANEL_W }} data-testid="tutor-shoutout-panel">
          {open && (
            <div className="bg-white border border-[#E6DDFB] rounded-xl shadow-lg p-3 text-[#2F1E6B] text-sm">
              <div className="font-semibold mb-2">Why? (the child sees this)</div>
              <select className="w-full border rounded px-2 py-1 mb-2" value={reason} onChange={(e) => setReason(e.target.value)}>
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input className="w-full border rounded px-2 py-1 mb-2" placeholder="…or write your own" value={custom} maxLength={200} onChange={(e) => setCustom(e.target.value)} />
              <div className="flex gap-2 justify-end">
                <button type="button" className="px-2 py-1 text-xs rounded bg-gray-100" onClick={() => setOpen(false)}>Cancel</button>
                <button type="button" className="px-2 py-1 text-xs rounded bg-[#5C3BD1] text-white disabled:opacity-60" disabled={busy} onClick={send}>Send +100 ✨</button>
              </div>
            </div>
          )}
          {status && <div className={`text-xs rounded-lg bg-white px-2 py-1 shadow ${status.ok ? 'text-green-700' : 'text-red-600'}`}>{status.text}</div>}
        </div>,
        document.body,
      )}
    </div>
  );
}
